import { applySheets } from "../../../app/utils/styleCache.js";

export const PAGE_SIZES = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const STORAGE_KEY = "tablePageSize";

export function getStoredPageSize() {
    try {
        const stored = Number(localStorage.getItem(STORAGE_KEY));
        return PAGE_SIZES.includes(stored) ? stored : DEFAULT_PAGE_SIZE;
    } catch {
        return DEFAULT_PAGE_SIZE;
    }
}

function storePageSize(size) {
    try { localStorage.setItem(STORAGE_KEY, String(size)); } catch { /* storage unavailable */ }
}

const REFRESH_EVENTS = [
    "pageLoaded", "dataLoaded", "dataProcessed", "dataFiltered", "renderComplete",
    "rowAdded", "rowDeleted",
];

export class AppPagination extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: "open" });
        this.table = null;
        this.refreshQueued = false;
    }

    connectedCallback() {
        this.shadowRoot.innerHTML = `
            <div class="bar">
                <span class="info" id="info"></span>
                <nav class="pages" id="pages" aria-label="Seiten"></nav>
                <label class="sizeBox">
                    <span>Pro Seite</span>
                    <select id="size">${PAGE_SIZES.map(size => `<option value="${size}">${size}</option>`).join("")}</select>
                </label>
            </div>
        `;
        this.ready = applySheets(this.shadowRoot, ["components/shared/app-pagination/app-pagination.css"]);

        this.shadowRoot.getElementById("pages").addEventListener("click", event => {
            const button = event.target.closest("button[data-page]");
            if (!button || button.disabled) return;
            this.table?.setPage(Number(button.dataset.page));
        });

        this.shadowRoot.getElementById("size").addEventListener("change", event => {
            const size = Number(event.target.value);
            storePageSize(size);
            this.table?.setPageSize(size);
        });

        this.render();
    }

    bind(table) {
        this.table = table;
        const refresh = () => this.queueRefresh();
        const start = () => {
            REFRESH_EVENTS.forEach(name => table.on(name, refresh));
            this.shadowRoot.getElementById("size").value = String(table.getPageSize());
            this.queueRefresh();
        };

        table.initialized ? start() : table.on("tableBuilt", start);
    }

    queueRefresh() {
        if (!this.refreshQueued) {
            this.refreshQueued = true;
            requestAnimationFrame(() => {
                this.refreshQueued = false;
                this.render();
            });
        }

        // Row events fire before Tabulator has updated its active rows, so render once more when things settled.
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.render(), 120);
    }

    render() {
        const table = this.table;
        if (!table?.initialized) return;

        const total = table.getDataCount("active");
        const size = table.getPageSize();
        const maxPage = Math.max(table.getPageMax() || 1, 1);
        const page = Math.min(Math.max(table.getPage() || 1, 1), maxPage);

        const from = total === 0 ? 0 : (page - 1) * size + 1;
        const to = Math.min(page * size, total);
        this.shadowRoot.getElementById("info").textContent = `${from}–${to} von ${total}`;
        this.shadowRoot.getElementById("size").value = String(size);
        this.shadowRoot.getElementById("pages").replaceChildren(...this.buildButtons(page, maxPage));
    }

    buildButtons(page, maxPage) {
        const buttons = [
            this.pageButton("‹", page - 1, { disabled: page <= 1, label: "Vorherige Seite" }),
        ];

        // At most SLOT_COUNT slots, never padded: the bar only changes when the number of pages changes, not while paging.
        for (const slot of pageSlots(page, maxPage)) {
            buttons.push(slot === GAP ? this.spacer("…") : this.pageButton(String(slot), slot, { active: slot === page }));
        }

        buttons.push(this.pageButton("›", page + 1, { disabled: page >= maxPage, label: "Nächste Seite" }));
        return buttons;
    }

    spacer(text) {
        const element = document.createElement("span");
        element.className = "slot";
        element.textContent = text;
        return element;
    }

    pageButton(text, page, { active = false, disabled = false, label = `Seite ${page}` } = {}) {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = text;
        button.dataset.page = String(page);
        button.disabled = disabled;
        button.setAttribute("aria-label", label);
        if (active) {
            button.classList.add("active");
            button.setAttribute("aria-current", "page");
        }
        return button;
    }
}

const SLOT_COUNT = 7;
const GAP = -1;

/** Page numbers for the middle slots, GAP = "…". All pages if there are at most SLOT_COUNT, otherwise exactly SLOT_COUNT entries. */
function pageSlots(page, maxPage) {
    if (maxPage <= SLOT_COUNT) {
        return Array.from({ length: maxPage }, (_, index) => index + 1);
    }
    if (page <= 4) return [1, 2, 3, 4, 5, GAP, maxPage];
    if (page >= maxPage - 3) return [1, GAP, maxPage - 4, maxPage - 3, maxPage - 2, maxPage - 1, maxPage];
    return [1, GAP, page - 1, page, page + 1, GAP, maxPage];
}

if (!customElements.get("app-pagination")) {
    customElements.define("app-pagination", AppPagination);
}
